import { RobotOutlined } from "@ant-design/icons"
import { Button, Card, Empty, Modal, Skeleton, Typography } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { listAnalyses } from "../api/services.ts"
import type { AnalysisRow } from "../api/types.ts"
import { formatDateTime } from "../format.ts"
import { StatusTag } from "../StatusTag.tsx"
import { useFpeAccess } from "./fpe-access.ts"
import { openFpeWizard, useFpeTask } from "./fpe-task-store.ts"
import { useStartPlan } from "./use-start-plan.ts"
import { useDraftMemories } from "./use-wizard-memory.ts"
import { derivedStep, knownStep, stepNumber } from "./wizard-steps.ts"

const { Text } = Typography
const p = "aiCenter.sowReceivables.recordPlans"

export interface PlanRecord {
  id: string
  kind: "lead" | "project"
  name: string
}

const FINISHED = new Set(["applied", "cancelled"])

/** The AI plans aimed at one lead or project — the same analysis records the AI
 *  Center lists. Re-read whenever the wizard closes or is minimized. */
export function useRecordPlans(recordId: string | undefined, enabled: boolean) {
  const [plans, setPlans] = useState<AnalysisRow[] | null>(null)
  const task = useFpeTask()
  const load = useCallback(async (): Promise<AnalysisRow[]> => {
    if (!recordId || !enabled) return []
    try {
      const page = await listAnalyses({ target: recordId, page_size: 50 })
      setPlans(page.results)
      return page.results
    } catch {
      setPlans([])
      return []
    }
  }, [recordId, enabled])
  const open = Boolean(task && !task.minimized)
  useEffect(() => {
    if (!open) void load()
  }, [load, open])
  return { plans, reload: load }
}

function PlanRows({ plans }: { plans: AnalysisRow[] }) {
  const { t } = useTranslation()
  const memories = useDraftMemories()
  return (
    <ul className="m-0 flex list-none flex-col divide-y divide-slate-100 p-0">
      {plans.map((plan) => {
        const step = knownStep(memories[plan.id]?.step) ?? derivedStep(plan)
        const files = plan.files.map((f) => f.original_name).join(", ")
        const action =
          plan.status === "applied"
            ? "view"
            : FINISHED.has(plan.status)
              ? "open"
              : "resume"
        return (
          <li
            key={plan.id}
            className="flex flex-wrap items-center justify-between gap-2 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-800">
                {plan.name ||
                  plan.files[0]?.original_name ||
                  t(`${p}.untitled`)}
              </div>
              <Text type="secondary" className="block truncate text-xs">
                {files || t(`${p}.noFiles`)}
              </Text>
              <Text type="secondary" className="text-xs">
                {plan.status === "applied"
                  ? t(`${p}.applied`)
                  : t(`${p}.step`, {
                      number: stepNumber(step),
                      label: t(`aiCenter.sowReceivables.wizard.steps.${step}`)
                    })}
                {" · "}
                {t(`${p}.updated`, { when: formatDateTime(plan.updated) })}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <StatusTag status={plan.status} />
              <Button size="small" onClick={() => openFpeWizard(plan.id)}>
                {t(`${p}.actions.${action}`)}
              </Button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/** The record header's "AI Project Plan" action. With no plan yet it starts one aimed
 *  at this record; otherwise it lists the record's plans to resume, view or add to,
 *  so a click never creates a duplicate draft by itself. */
export function AiPlanButton({ record }: { record: PlanRecord }) {
  const { t } = useTranslation()
  const { canView, canStart } = useFpeAccess()
  const { plans, reload } = useRecordPlans(record.id, canView)
  const { start, starting } = useStartPlan()
  const [listing, setListing] = useState(false)
  if (!canView) return null
  const click = async () => {
    const current = await reload()
    if (current.length === 0) {
      if (canStart) await start({ target: record.id, lockTarget: true })
      return
    }
    setListing(true)
  }
  return (
    <>
      <Button
        icon={<RobotOutlined aria-hidden />}
        loading={starting}
        onClick={() => void click()}
      >
        {t(`${p}.button`)}
      </Button>
      <Modal
        open={listing}
        onCancel={() => setListing(false)}
        title={t(`${p}.title`, { name: record.name })}
        footer={
          canStart ? (
            <Button
              type="primary"
              loading={starting}
              onClick={() => {
                setListing(false)
                void start({ target: record.id, lockTarget: true })
              }}
            >
              {t(`${p}.startAnother`)}
            </Button>
          ) : null
        }
      >
        <Text type="secondary">{t(`${p}.chooseHelp`)}</Text>
        {plans ? (
          <div onClickCapture={() => setListing(false)}>
            <PlanRows plans={plans} />
          </div>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  )
}

/** A compact card of the record's AI plans, for the record's own workspace. */
export function AiProjectPlansCard({ record }: { record: PlanRecord }) {
  const { t } = useTranslation()
  const { canView, canStart } = useFpeAccess()
  const { plans } = useRecordPlans(record.id, canView)
  const { start, starting } = useStartPlan()
  if (!canView) return null
  return (
    <Card
      size="small"
      title={
        <span className="inline-flex items-center gap-2">
          <RobotOutlined aria-hidden />
          {t(`${p}.cardTitle`)}
        </span>
      }
      extra={
        canStart ? (
          <Button
            size="small"
            loading={starting}
            onClick={() => void start({ target: record.id, lockTarget: true })}
          >
            {plans?.length ? t(`${p}.startAnother`) : t(`${p}.start`)}
          </Button>
        ) : null
      }
    >
      {plans === null ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : plans.length ? (
        <PlanRows plans={plans} />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(`${p}.empty`)}
        />
      )}
    </Card>
  )
}
